import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, X, Send, Database, Code2, Braces } from "lucide-react";
import ExpressionBuilder from "@/components/ExpressionBuilder";
import { toast } from "sonner";

/**
 * Advanced configuration modal used for httpAction, sqlEnrichment, customScript nodes.
 * Also used for detection engine's action step (HTTP webhook).
 */
export default function NodeConfigDialog({ open, onOpenChange, node, onSave, mode = "workflow" }) {
  const [local, setLocal] = useState(node?.data || {});

  React.useEffect(() => {
    if (open) setLocal(node?.data || {});
  }, [open, node]);

  if (!node) return null;

  const update = (patch) => setLocal((l) => ({ ...l, ...patch }));

  const save = () => {
    onSave(local);
    onOpenChange(false);
    toast.success("Configuration saved");
  };

  const type = node.type;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="node-config-dialog">
        <DialogHeader>
          <div className="label-caps">{typeLabel(type)}</div>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            {typeIcon(type)}
            <input
              value={local.label || ""}
              onChange={(e) => update({ label: e.target.value })}
              className="bg-transparent outline-none flex-1"
              data-testid="config-dialog-label"
            />
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto -mx-6 px-6">
          {(type === "httpAction" || type === "detectionAction") && (
            <HttpConfig data={local} update={update} />
          )}
          {type === "sqlEnrichment" && <SqlConfig data={local} update={update} />}
          {type === "customScript" && <PythonConfig data={local} update={update} />}
          {type === "ifCondition" && (
            <div className="space-y-4 py-2">
              <ExpressionBuilder
                label="Branch condition"
                value={local.expression || ""}
                onChange={(v) => update({ expression: v })}
                rows={5}
                testId="dialog-condition"
              />
              <div className="text-xs text-muted-foreground border border-border rounded-md p-3 bg-secondary/40">
                <div className="label-caps mb-1">Preview</div>
                <div className="font-mono">
                  IF <span className="text-primary">{local.expression || "…"}</span> → TRUE path
                </div>
                <div className="font-mono">
                  ELSE → FALSE path
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="config-dialog-cancel">Cancel</Button>
          <Button onClick={save} className="bg-primary hover:bg-[#0B2B4E]" data-testid="config-dialog-save">Save configuration</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- HTTP Config ---------- */
function HttpConfig({ data, update }) {
  const headers = data.headers || [{ k: "Content-Type", v: "application/json" }];
  const params = data.params || [];
  const setHeaders = (h) => update({ headers: h });
  const setParams = (p) => update({ params: p });

  return (
    <Tabs defaultValue="request" className="w-full py-2">
      <TabsList className="bg-secondary/60">
        <TabsTrigger value="request" data-testid="http-tab-request">Request</TabsTrigger>
        <TabsTrigger value="headers" data-testid="http-tab-headers">Headers ({headers.length})</TabsTrigger>
        <TabsTrigger value="params" data-testid="http-tab-params">Query params ({params.length})</TabsTrigger>
        <TabsTrigger value="body" data-testid="http-tab-body">Body</TabsTrigger>
        <TabsTrigger value="auth" data-testid="http-tab-auth">Auth</TabsTrigger>
      </TabsList>

      <TabsContent value="request" className="space-y-4 pt-4">
        <div className="grid grid-cols-[120px_1fr] gap-3">
          <div>
            <Label className="label-caps mb-1.5 block">Method</Label>
            <Select value={data.method || "POST"} onValueChange={(v) => update({ method: v })}>
              <SelectTrigger data-testid="http-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["GET", "POST", "PUT", "PATCH", "DELETE"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-caps mb-1.5 block">URL (personalization supported)</Label>
            <Input
              value={data.url || ""}
              onChange={(e) => update({ url: e.target.value })}
              placeholder="https://api.example.com/{{ event.customer_code }}"
              className="font-mono text-xs"
              data-testid="http-url"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-caps mb-1.5 block">Timeout (ms)</Label>
            <Input type="number" value={data.timeout || 5000} onChange={(e) => update({ timeout: Number(e.target.value) })} data-testid="http-timeout" />
          </div>
          <div>
            <Label className="label-caps mb-1.5 block">Retry policy</Label>
            <Select value={data.retry || "none"} onValueChange={(v) => update({ retry: v })}>
              <SelectTrigger data-testid="http-retry"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="linear_3">Linear · 3 attempts</SelectItem>
                <SelectItem value="expo_5">Exponential · 5 attempts</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </TabsContent>

      <TabsContent value="headers" className="pt-4">
        <KVEditor items={headers} onChange={setHeaders} testIdPrefix="http-header" placeholderKey="X-Api-Key" placeholderValue="{{ secret.api_key }}" />
      </TabsContent>

      <TabsContent value="params" className="pt-4">
        <KVEditor items={params} onChange={setParams} testIdPrefix="http-param" placeholderKey="customer_id" placeholderValue="{{ event.customer_code }}" />
      </TabsContent>

      <TabsContent value="body" className="space-y-3 pt-4">
        <div className="flex items-center gap-2">
          <Label className="label-caps">Content type</Label>
          <Select value={data.body_type || "json"} onValueChange={(v) => update({ body_type: v })}>
            <SelectTrigger className="w-40 h-8" data-testid="http-body-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="json">application/json</SelectItem>
              <SelectItem value="form">form-urlencoded</SelectItem>
              <SelectItem value="raw">raw text</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <ExpressionBuilder
          label="Request body"
          value={data.body || `{\n  "customer": "{{ event.customer_code }}",\n  "type": "{{ event.event_type }}",\n  "amount": {{ event.payload.amount }}\n}`}
          onChange={(v) => update({ body: v })}
          rows={10}
          testId="http-body"
        />
      </TabsContent>

      <TabsContent value="auth" className="space-y-3 pt-4">
        <div>
          <Label className="label-caps mb-1.5 block">Auth type</Label>
          <Select value={data.auth_type || "none"} onValueChange={(v) => update({ auth_type: v })}>
            <SelectTrigger data-testid="http-auth-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="bearer">Bearer token</SelectItem>
              <SelectItem value="basic">Basic auth</SelectItem>
              <SelectItem value="api_key">API key header</SelectItem>
              <SelectItem value="oauth2">OAuth2 client credentials</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {data.auth_type === "bearer" && (
          <div>
            <Label className="label-caps mb-1.5 block">Token (from secret)</Label>
            <Input value={data.auth_token || ""} onChange={(e) => update({ auth_token: e.target.value })} placeholder="{{ secret.notify_api_token }}" className="font-mono text-xs" data-testid="http-auth-token" />
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
}

function KVEditor({ items, onChange, testIdPrefix, placeholderKey, placeholderValue }) {
  const update = (idx, patch) => onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, { k: "", v: "" }]);

  return (
    <div className="space-y-2">
      {items.length === 0 && (
        <div className="text-xs text-muted-foreground py-4 text-center border border-dashed border-border rounded-md">No entries</div>
      )}
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2">
          <Input value={it.k} onChange={(e) => update(i, { k: e.target.value })} placeholder={placeholderKey} className="font-mono text-xs" data-testid={`${testIdPrefix}-k-${i}`} />
          <Input value={it.v} onChange={(e) => update(i, { v: e.target.value })} placeholder={placeholderValue} className="font-mono text-xs" data-testid={`${testIdPrefix}-v-${i}`} />
          <Button variant="ghost" size="icon" onClick={() => remove(i)} className="h-9 w-9 text-destructive"><X className="w-3.5 h-3.5" /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={add} className="gap-1 text-xs" data-testid={`${testIdPrefix}-add`}>
        <Plus className="w-3 h-3" /> Add
      </Button>
    </div>
  );
}

/* ---------- SQL Config ---------- */
function SqlConfig({ data, update }) {
  return (
    <Tabs defaultValue="connection" className="w-full py-2">
      <TabsList className="bg-secondary/60">
        <TabsTrigger value="connection" data-testid="sql-tab-conn">Connection</TabsTrigger>
        <TabsTrigger value="query" data-testid="sql-tab-query">Query</TabsTrigger>
        <TabsTrigger value="output" data-testid="sql-tab-output">Output</TabsTrigger>
      </TabsList>

      <TabsContent value="connection" className="space-y-3 pt-4">
        <div>
          <Label className="label-caps mb-1.5 block">Dataset</Label>
          <Select value={data.dataset || "core_customer"} onValueChange={(v) => update({ dataset: v })}>
            <SelectTrigger data-testid="sql-dataset"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="core_customer">Core Banking · Customer Master</SelectItem>
              <SelectItem value="crm">CRM · Contact &amp; Preferences</SelectItem>
              <SelectItem value="attribution">Marketing Attribution</SelectItem>
              <SelectItem value="legacy_lo">Legacy Loan Origination</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-caps mb-1.5 block">Read timeout (ms)</Label>
            <Input type="number" value={data.timeout || 3000} onChange={(e) => update({ timeout: Number(e.target.value) })} data-testid="sql-timeout" />
          </div>
          <div>
            <Label className="label-caps mb-1.5 block">Cache TTL (s)</Label>
            <Input type="number" value={data.cache_ttl || 0} onChange={(e) => update({ cache_ttl: Number(e.target.value) })} data-testid="sql-cache" />
          </div>
        </div>
      </TabsContent>

      <TabsContent value="query" className="space-y-3 pt-4">
        <ExpressionBuilder
          label="SQL query (bind :customer_code, :event_id)"
          value={data.query || "SELECT risk_score, segment\nFROM crm.customer\nWHERE code = :customer_code"}
          onChange={(v) => update({ query: v })}
          rows={9}
          testId="sql-query"
          placeholder="SELECT ... FROM ... WHERE code = :customer_code"
        />
      </TabsContent>

      <TabsContent value="output" className="space-y-3 pt-4">
        <div>
          <Label className="label-caps mb-1.5 block">Output variable name</Label>
          <Input value={data.variable || "enrich"} onChange={(e) => update({ variable: e.target.value })} placeholder="enrich" className="font-mono text-xs" data-testid="sql-variable" />
          <div className="text-xs text-muted-foreground mt-2">
            Accessed downstream as <code className="font-mono text-primary">{`{{ ${data.variable || "enrich"}.risk_score }}`}</code>
          </div>
        </div>
        <div>
          <Label className="label-caps mb-1.5 block">On empty result</Label>
          <Select value={data.on_empty || "continue"} onValueChange={(v) => update({ on_empty: v })}>
            <SelectTrigger data-testid="sql-on-empty"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="continue">Continue with null</SelectItem>
              <SelectItem value="skip">Skip downstream nodes</SelectItem>
              <SelectItem value="fail">Fail workflow run</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </TabsContent>
    </Tabs>
  );
}

/* ---------- Python Config ---------- */
function PythonConfig({ data, update }) {
  return (
    <Tabs defaultValue="code" className="w-full py-2">
      <TabsList className="bg-secondary/60">
        <TabsTrigger value="code" data-testid="py-tab-code">Code</TabsTrigger>
        <TabsTrigger value="variables" data-testid="py-tab-vars">Available variables</TabsTrigger>
        <TabsTrigger value="output" data-testid="py-tab-output">Output mapping</TabsTrigger>
      </TabsList>

      <TabsContent value="code" className="space-y-3 pt-4">
        <div className="border border-border rounded-md overflow-hidden bg-[hsl(var(--secondary))]">
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-border">
            <div className="flex items-center gap-2 text-xs">
              <Code2 className="w-3 h-3 text-primary" />
              <span className="font-mono">script.py</span>
            </div>
            <div className="text-[0.65rem] text-muted-foreground">Python 3.11 · sandboxed</div>
          </div>
          <Textarea
            value={data.code || "# Available: event, customer, enrich, secret\n# Set result variable(s) to expose downstream\n\nrisk_adjusted = customer.risk_score * 1.15\nif event.payload.get('amount', 0) > 10000:\n    tier = 'high'\nelse:\n    tier = 'standard'\n\nresult = { 'risk_adjusted': risk_adjusted, 'tier': tier }"}
            onChange={(e) => update({ code: e.target.value })}
            rows={16}
            className="font-mono text-xs leading-relaxed border-0 rounded-none focus-visible:ring-0 bg-transparent"
            data-testid="py-code"
          />
        </div>
      </TabsContent>

      <TabsContent value="variables" className="pt-4">
        <div className="border border-border rounded-md overflow-hidden">
          {[
            { name: "event", type: "object", desc: "The incoming event: event_id, event_type, customer_code, event_source, event_datetime, payload" },
            { name: "customer", type: "object", desc: "Customer profile (from Core Banking): code, segment, country, risk_score, lifetime_value" },
            { name: "enrich", type: "object", desc: "Output of upstream Data Enrichment node (if any)" },
            { name: "secret", type: "object", desc: "Encrypted secrets store: secret.api_key etc." },
            { name: "log", type: "logger", desc: "log.info(), log.warning(), log.error() → OpenTelemetry sink" },
          ].map((v) => (
            <div key={v.name} className="grid grid-cols-[140px_80px_1fr] gap-3 px-3 py-2 border-b border-border last:border-0 text-xs">
              <code className="font-mono text-primary">{v.name}</code>
              <span className="text-muted-foreground font-mono">{v.type}</span>
              <span className="text-muted-foreground">{v.desc}</span>
            </div>
          ))}
        </div>
      </TabsContent>

      <TabsContent value="output" className="space-y-3 pt-4">
        <div>
          <Label className="label-caps mb-1.5 block">Output variable name</Label>
          <Input value={data.output_var || "result"} onChange={(e) => update({ output_var: e.target.value })} className="font-mono text-xs" data-testid="py-out-var" />
        </div>
        <div>
          <Label className="label-caps mb-1.5 block">Max execution time (ms)</Label>
          <Input type="number" value={data.max_ms || 500} onChange={(e) => update({ max_ms: Number(e.target.value) })} data-testid="py-max-ms" />
        </div>
      </TabsContent>
    </Tabs>
  );
}

const typeIcon = (t) => {
  const cls = "w-5 h-5 text-primary";
  if (t === "httpAction" || t === "detectionAction") return <Send className={cls} />;
  if (t === "sqlEnrichment") return <Database className={cls} />;
  if (t === "customScript") return <Code2 className={cls} />;
  if (t === "ifCondition") return <Braces className={cls} />;
  return null;
};

const typeLabel = (t) => ({
  httpAction: "Action · HTTP Request",
  detectionAction: "Detection Action · HTTP Webhook",
  sqlEnrichment: "Data Enrichment · SQL",
  customScript: "Compute · Python Script",
  ifCondition: "Branch · If Condition",
}[t] || "Advanced configuration");
